/**
 * Main Theme JavaScript
 * 
 * Core functionality and initialization for the theme
 */
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Lenis from 'lenis'
gsap.registerPlugin(ScrollTrigger);


import { initCart } from './utils/cart.js';
import { loadTemplateScripts } from './utils/templates.js';
import { loadSectionScripts } from './utils/sections.js';
import { loadSnippetScripts } from './utils/snippets.js';
import { initInfiniteScroll } from './utils/infinite-scroll.js';
import { initFilterChips } from './utils/filter-chips.js';
import { initCookieBanner } from './utils/cookie-banner.js';

import { horizontalLoop } from './snippets/horizontalLoop'

class Theme {
  constructor() {
    this.lastScroll = 0;
    this.currentScroll = window.pageYOffset;
    this.init();
  }

  init() {
    // Initialize core functionality
    this.initAccessibility();
    this.initScrollEffects();

    // Initialize cart functionality
    initCart();

    // Single-select product-type chips, wherever they appear on the page
    // (collection, poster collection, tag views, designer/brand/publisher).
    initFilterChips();

    // Letter-flip hover on Shopify's cookie banner / preferences buttons.
    initCookieBanner();

    // Load template-specific scripts dynamically
    loadTemplateScripts();

    // Load section-specific scripts dynamically
    loadSectionScripts();

    // Load snippet-specific scripts dynamically
    loadSnippetScripts();

    // Wire up infinite-scroll behaviour for any [data-infinite-scroll] grids
    initInfiniteScroll();


    // Announce theme is ready. core/html.scss keeps the [data-reveal] /
    // [data-collapse] elements in their pre-JS state until this class lands,
    // so it must come after every loader above has applied its from-states.
    document.documentElement.classList.add('theme-ready');
    document.dispatchEvent(new CustomEvent('theme:loaded'));
  }

  initAccessibility() {
    // Focus visible handling
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.documentElement.classList.add('using-keyboard');
      }
    });

    document.addEventListener('mousedown', () => {
      document.documentElement.classList.remove('using-keyboard');
    });
  }

  initScrollEffects() {

    this.checkScroll();

    // Touch-only devices (phones, tablets) scroll natively, and Lenis adds
    // nothing there — but it still registers non-passive touchstart/touchmove
    // listeners on the whole page. iOS Safari then has to wait for the main
    // thread on every touch step, so any hitch (a ScrollTrigger refresh, a
    // header transition, an image decode) becomes a visible scroll stall.
    // Skip Lenis entirely; every consumer of window.lenis has a native
    // fallback, and ScrollTrigger drives itself from the native scroll event.
    if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
      window.lenis = null;
      window.addEventListener('scroll', this.checkScroll, { passive: true });
      return;
    }

    // Initialize a new Lenis instance for smooth scrolling
    window.lenis = new Lenis({
      lerp: 0.1,
      wheelMultiplier: 0.9,
      gestureOrientation: "vertical",
      normalizeWheel: true,
      smoothTouch: false,
      autoRaf: false,
    });

    // Synchronize Lenis scrolling with GSAP's ScrollTrigger plugin
    window.lenis.on('scroll', (e) => {
      this.checkScroll();
      ScrollTrigger.update();
    });
    // This ensures Lenis's smooth scroll animation updates on each GSAP tick
    gsap.ticker.add((time) => {
      window.lenis.raf(time * 1000); // Convert time from seconds to milliseconds
    });
    // Disable lag smoothing in GSAP to prevent any delay in scroll animations
    gsap.ticker.lagSmoothing(0);

  }

  checkScroll = () => {
    this.currentScroll = window.pageYOffset;
    if (this.currentScroll > this.lastScroll && this.currentScroll > 10) {
      document.documentElement.classList.add('scroll-down');
      document.documentElement.classList.remove('scroll-up');
    } else {
      document.documentElement.classList.add('scroll-up');
      document.documentElement.classList.remove('scroll-down');
    }
    if (this.currentScroll > 1) {
      document.documentElement.classList.add('scrolled');
    } else {
      document.documentElement.classList.remove('scrolled');
    }
    this.lastScroll = this.currentScroll;
  }
}

// Export the Theme class for external use
export default Theme;

// Only run initialization once, even if this module is imported multiple times
// This prevents double initialization when main.js is imported by other chunks
if (!window.__themeInitialized) {
  window.__themeInitialized = true;
  window.__themeInstance = null;

  function initTheme() {
    // Prevent re-initialization if already initialized
    if (window.__themeInstance) {
      return;
    }

    window.__themeInstance = new Theme();
    window.__theme = window.__themeInstance; // For debugging
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTheme);
  } else {
    initTheme();
  }

  // Hot Module Replacement (HMR) support for development
  if (import.meta.hot) {
    import.meta.hot.accept(() => {
      window.__themeInstance = null; // Clear for HMR
      initTheme();
    });
  }
}

